import { useCallback } from "react";

import {
  InfiniteData,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import _ from "lodash";
import { useAppDispatch } from "src/redux/hooks";
import {
  OrderVariantErrorType,
  removeOrderVariantError,
  setOrderVariantError,
} from "src/redux/slices/ordering/orderSetSlice";

import { budgetsKeyFactory } from "@features/budgets/queries";
import { useMutateError } from "@features/errors";
import { OrderCosts } from "@features/orders/helpers";
import { useStatesQuery } from "@features/states";
import { Order } from "@models/Order";
import { OrderSet } from "@models/OrderSet";
import { OrderVariant } from "@models/OrderVariant";
import client from "@services/api";

import { orderSetsKeyFactory } from "../../../queries/orderSetQueries";
import useOrderSetId from "../useOrderSetId";

const useWarehouseTaxRate = () => {
  const { data } = useStatesQuery({ filter: { code: "IL" } });
  const illinoiseTaxRate = data?.[0]?.taxRate ?? 9.08;
  return illinoiseTaxRate;
};

const updateOrderTotals = (
  order: Order,
  oldOV: OrderVariant,
  newOV: OrderVariant
): Order => {
  const updateKey = (key: string) =>
    String(+order[key] - +oldOV[key] + +newOV[key]);

  return Object.assign(order, {
    totalQuantity: order.totalQuantity - oldOV.qty + newOV.qty,
    totalBeaconCost: updateKey("totalBeaconCost"),
    totalEstimatedCost: updateKey("totalEstimatedCost"),
    totalEstimatedTax: updateKey("totalEstimatedTax"),
    billableTotalBeaconCost: updateKey("billableTotalBeaconCost"),
    totalEstimatedShippingCost: updateKey("totalEstimatedShippingCost"),
    billableTotalEstimatedCost: updateKey("billableTotalEstimatedCost"),
    billableTotalEstimatedTax: updateKey("billableTotalEstimatedTax"),
    billableTotalEstimatedShippingCost: updateKey(
      "billableTotalEstimatedShippingCost"
    ),
  });
};

const calcOVCostsAfterPricingTierChange = (
  orderVariant: OrderVariant,
  referenceOrderVariant: OrderVariant,
  taxRate: number,
  shippingPercent: number
) => {
  const newCost = +referenceOrderVariant.estimatedCost;

  const newTotalCost = orderVariant.qty * newCost;
  const newTotalTax = (newTotalCost * taxRate) / 100;
  const newTotalShippingCost = newTotalCost * shippingPercent;

  const newBillableValue = (
    field: keyof OrderCosts,
    newEstimateValue: number | string
  ) => {
    // The billable value is either 0, or equal to the estimated value
    // it's also possible that billable value is 0 because estimated value is 0
    // If the new billable value is to not be 0, either the orderVariant billable value
    // or the referenceOrderVariant billable will not be 0
    if (+referenceOrderVariant[field] === 0 && +orderVariant[field] === 0) {
      return "0";
    }

    return newEstimateValue.toString();
  };

  return {
    estimatedCost: String(newCost),
    totalEstimatedCost: String(newTotalCost),
    totalEstimatedTax: String(newTotalTax),
    totalEstimatedShippingCost: String(newTotalShippingCost),
    billableTotalEstimatedCost: newBillableValue(
      "billableTotalEstimatedCost",
      newTotalCost
    ),
    billableTotalEstimatedTax: newBillableValue(
      "billableTotalEstimatedTax",
      newTotalTax
    ),
    billableTotalEstimatedShippingCost: newBillableValue(
      "billableTotalEstimatedShippingCost",
      newTotalShippingCost
    ),
  };
};

type SetOrderVariantQtyPayload = {
  id: string;
  qty: number;
};

export default function useSetOrderVariantQty() {
  const dispatch = useAppDispatch();
  const setError = useMutateError();
  const queryClient = useQueryClient();
  const orderSetId = useOrderSetId();

  const warehouseTaxRate = useWarehouseTaxRate();

  const startOrderSet = useCallback(async () => {
    client.post(`order-sets/${orderSetId}/start`).catch((err) => {
      console.error(err);
      queryClient.invalidateQueries({
        queryKey: orderSetsKeyFactory.detail(orderSetId).queryKey,
      });
    });
    // Optimistic update
    queryClient.setQueryData(
      orderSetsKeyFactory.detail(orderSetId).queryKey,
      (orderSet: OrderSet) => ({
        ...orderSet,
        status: "in-progress",
      })
    );
    queryClient.setQueryData(
      orderSetsKeyFactory.paginated._def,
      (data: InfiniteData<OrderSet>) => {
        const orderSet = data?.pages.find((p) => p.id === orderSetId);
        if (orderSet) {
          return {
            ...data,
            pages: data.pages.map((p) =>
              p.id === orderSetId ? { ...p, status: "in-progress" } : p
            ),
          };
        }
        return data;
      }
    );
  }, [orderSetId, queryClient]);

  return useMutation({
    mutationFn: ({ id, qty }: SetOrderVariantQtyPayload) => {
      const orderSet = queryClient.getQueryData<OrderSet>(
        orderSetsKeyFactory.detail(orderSetId).queryKey
      )!;

      if (orderSet.status === "inactive") {
        startOrderSet();
      }

      return client
        .update<OrderVariant>(`order-variants/${id}`, {
          type: "order-variant",
          qty,
        })
        .then((data) => ({ orderVariant: data.data, orderSet }));
    },
    onSuccess: ({ orderVariant: newOrderVariant, orderSet }) => {
      dispatch(removeOrderVariantError({ id: newOrderVariant.id }));

      queryClient.invalidateQueries({
        // Root key: covers paginated lists and forOrderSet table side-loads.
        queryKey: budgetsKeyFactory._def,
      });

      const osVariant = orderSet.orderSetVariants.find(
        (osv) => osv.variant.id === newOrderVariant.variant.id
      )!;
      const pricingTierChange =
        osVariant.estimatedCost !== newOrderVariant.estimatedCost;

      if (pricingTierChange) {
        queryClient.setQueryData<OrderSet>(
          orderSetsKeyFactory.detail(orderSetId).queryKey,
          (orderSet) =>
            orderSet && {
              ...orderSet,
              orderSetVariants: orderSet.orderSetVariants.map((osv) => {
                if (osv.variant.id === newOrderVariant.variant.id) {
                  return {
                    ...osv,
                    estimatedCost: newOrderVariant.estimatedCost,
                  };
                }
                return osv;
              }),
            }
        );
      }
      return queryClient.setQueryData(
        orderSetsKeyFactory.detail(orderSetId)._ctx.orders.queryKey,
        (orders: Order[]) => {
          return orders.map((order) => {
            // There are two cases where we want to update the orderVariant:
            // 1. We're replacing this order-variant with the new updated one.
            // 2. The pricing tier of the variant for this order-set has changed, all order-variants for this variant need to be updated
            const orderVariants = order.orderVariants.map((ov) => {
              // We're replacing this order-variant with the new updated one.
              if (ov.id === newOrderVariant.id) {
                updateOrderTotals(order, ov, newOrderVariant);
                return newOrderVariant;
              }

              // The pricing tier of the item has changed, all order-variants for this item need to be updated
              if (
                pricingTierChange &&
                ov.variant.item.id === newOrderVariant.variant.item.id
              ) {
                const newOV = {
                  ...ov,
                  ...calcOVCostsAfterPricingTierChange(
                    ov,
                    newOrderVariant,
                    +(order.address?.state.taxRate ?? warehouseTaxRate),
                    +orderSet.estimatedShippingPercent
                  ),
                  variablePricingMinRangeValue:
                    newOrderVariant.variablePricingMinRangeValue,
                  variablePricingMaxRangeValue:
                    newOrderVariant.variablePricingMaxRangeValue,
                };
                updateOrderTotals(order, ov, newOV);
                return newOV;
              }

              return ov;
            });

            return {
              ...order,
              orderVariants,
            };
          });
        }
      );
    },
    onError: (error: AxiosError<{ errors: { title: string }[] }>, { id }) => {
      if (Array.isArray(error.response?.data?.errors)) {
        const message = error.response!.data.errors[0]?.title;
        if (message.includes("exceeds maximum available inventory")) {
          // matches a number in parentheses at the end of the string
          const maxQuantity = message.match(/\((\d+)\)$/)?.[1];
          dispatch(
            setOrderVariantError({
              id,
              error: message,
              maxQuantity: maxQuantity ? +maxQuantity : null,
              type: OrderVariantErrorType.MAX_QUANTITY,
            })
          );
        } else {
          dispatch(
            setOrderVariantError({
              id,
              error: message,
              type: message.includes("over budget")
                ? OrderVariantErrorType.OVER_BUDGET
                : OrderVariantErrorType.OTHER,
            })
          );
        }
      } else {
        setError(error, "useSetOrderVariantQty");
      }
    },
  });
}
